/**
 * Dinámica de IMPULSO — el cierre de turno.
 *
 * La distinción que ordena todo la puso el usuario el 2026-09-04: **el canje es un tipo de
 * dinámica y esta es otro**. En la de canje se registra la compra de un cliente; acá no hay canje
 * que registrar porque en la tienda no dejan usar el celular — *«solo medimos lo que la
 * impulsadora puede decirnos»*.
 *
 * De eso salen las tres reglas de diseño de este formulario:
 *
 *  1. **Se llena UNA vez, al terminar el turno.** No hay registro durante la jornada.
 *  2. **Todo se contesta de memoria o mirando la góndola al cerrar.** Por eso casi todo es opción
 *     cerrada: lo que se recuerda mal son los matices, no las categorías.
 *  3. **Las ventas se apoyan en papel.** La impulsadora lleva palotes durante el turno y los pasa
 *     acá. Es el único dato que se cruza contra el sell-out del cliente, así que tiene que ser un
 *     número contado, no una estimación.
 *
 * Los SKU NO están acá: viven en la tabla `impulso_skus` por campaña (0090), para que la próxima
 * campaña sea cargar filas y no programar.
 */

export interface Opcion {
  valor: string;
  etiqueta: string;
}

/** ¿Faltó producto en góndola? */
export const QUIEBRE: readonly Opcion[] = [
  { valor: 'ninguno', etiqueta: 'No faltó nada' },
  { valor: 'algunos', etiqueta: 'Faltaron algunos productos' },
  { valor: 'casi_todo', etiqueta: 'Faltó casi todo' },
];

/** ¿Hubo exhibición además de la góndola? */
export const EXHIBICION: readonly Opcion[] = [
  { valor: 'no', etiqueta: 'Solo la góndola' },
  { valor: 'cabecera', etiqueta: 'Cabecera de góndola' },
  { valor: 'isla', etiqueta: 'Isla' },
  { valor: 'caja', etiqueta: 'Zona de cajas' },
  { valor: 'otra', etiqueta: 'Otra ubicación' },
];

/** ¿Había impulsadora de otra marca? */
export const COMPETENCIA: readonly Opcion[] = [
  { valor: 'no', etiqueta: 'No vi a nadie de otra marca' },
  { valor: 'union', etiqueta: 'Sí, de Unión' },
  { valor: 'otra', etiqueta: 'Sí, de otra marca' },
];

/**
 * Las objeciones que se escucharon. **Se marcan VARIAS y cada una lleva su explicación.**
 *
 * Nació como desplegable de opción única y el usuario lo corrigió el 2026-09-05 mirando la
 * pantalla, con dos observaciones que son la misma:
 *
 *  · *«Puede ser que te hayan dicho sobre el precio, y que también no sabían cómo prepararlo, y que
 *    también buscaban algo sin gluten… ¿cómo marco los tres?»* — con una sola opción, dos de esas
 *    tres desaparecen y el turno se lee más simple de lo que fue.
 *  · *«Solamente pone el precio, pero ¿de qué?»* — y el ejemplo que dio es justo el dato que se
 *    perdía: *«una promoción que el cliente dice que vio en la otra marca»*.
 *
 * Por eso cada opción trae su `pregunta`: la que se le hace a la impulsadora DESPUÉS de marcarla.
 * No es adorno. «Comentario» a secas se contesta con un guion; «¿el precio de cuál producto?» se
 * contesta con el producto.
 *
 * «Prefiere pan de molde» sigue en la lista a propósito: es la señal de que el discurso se está
 * yendo al territorio del desayuno, que es justo lo que la capacitación prohíbe. Si esta opción
 * sube en una tienda, el problema es de discurso y no de producto.
 */
export interface OpcionDeObjecion extends Opcion {
  /** Lo que se le pregunta al marcarla. Vacío en «ninguna», que no abre nada. */
  pregunta: string;
  /**
   * De qué cuestionario es. Sin marcar, está en todos.
   *
   * La lista queda ÚNICA aunque los cuestionarios sean distintos, porque la bandeja de
   * oficina tiene que poder ponerle rótulo a lo guardado de cualquier campaña. Si se
   * partiera en listas sueltas, la bandeja mostraría el valor crudo de la otra.
   */
  solo?: 'fijas' | 'vital' | 'takis' | 'pg';
  /**
   * Ya no se ofrece en ningún cuestionario, pero se queda en la lista para que la bandeja siga
   * rotulando los cierres que la marcaron (y la ventana de oficina la muestra si está guardada).
   */
  retirada?: true;
}

export const OBJECION: readonly OpcionDeObjecion[] = [
  {
    valor: 'ninguna',
    etiqueta: 'Ninguna en particular',
    pregunta: '',
  },
  {
    valor: 'precio',
    etiqueta: 'El precio',
    pregunta: '¿El precio de cuál producto? ¿Lo comparó con alguna promoción que vio?',
  },
  {
    valor: 'no_conoce',
    etiqueta: 'No conocía el producto',
    pregunta: '¿Qué le llamó la atención cuando se lo mostraste?',
  },
  {
    valor: 'no_sabe_preparar',
    etiqueta: 'No sabía cómo prepararlo',
    pregunta: '¿Qué le explicaste? ¿Con qué duda se quedó?',
    solo: 'fijas',
  },
  /*
   * Las tres de abajo son de Vital y Cero Cero, y salen de la capacitación de Bimbo del
   * 11-set, no de la imaginación:
   *
   *  · EL SABOR. La masa madre tiene un perfil distinto y el propio Sergio lo advirtió:
   *    «quizás es el que tiene más problemas en gustos». Es la objeción que más se va a
   *    escuchar y no existía.
   *  · SU PAN DE SIEMPRE. Es la objeción real cuando le ofreces cambiar de pan a alguien
   *    que ya tiene el suyo. Reemplaza a «prefiere pan de molde», que acá no dice nada
   *    porque lo que vendemos ES pan de molde.
   *  · DIABETES. Bimbo lo marcó como línea legal: no se puede recomendar para diabéticos,
   *    solo nombrar los atributos. Saber cuántas veces lo preguntan sirve para afinar el
   *    discurso y para respaldarse si alguien dice que se recomendó.
   */
  {
    valor: 'sabor',
    etiqueta: 'No le gustó el sabor',
    pregunta: '¿Lo probó? ¿Qué le pareció: muy seco, muy ácido, otra cosa?',
    solo: 'vital',
  },
  {
    valor: 'su_pan',
    etiqueta: 'Prefiere su pan de siempre',
    pregunta: '¿Cuál lleva normalmente? ¿De qué marca?',
    solo: 'vital',
  },
  {
    valor: 'diabetes',
    etiqueta: 'Preguntó si es para diabéticos',
    pregunta: '¿Qué le respondiste? Recuerda: solo los atributos, sin recomendar.',
    solo: 'vital',
    // Era de Cero Cero (sin azúcar). Desde el 24-oct Vital se mide sin Cero Cero y la objeción
    // sale del cierre (usuario, 23-oct).
    retirada: true,
  },
  {
    solo: 'fijas',
    valor: 'prefiere_pan',
    etiqueta: 'Prefiere pan de molde',
    pregunta: '¿Qué te dijo del pan de molde?',
  },
  /*
   * La de Takis (pedida por el usuario el 9-oct). Es un snack picante: «muy picante» es su
   * objeción propia, como el sabor lo es de Vital. La pregunta busca el sabor que SÍ le gustó,
   * que es el dato para saber cuál ofrecer primero.
   */
  {
    valor: 'picante',
    etiqueta: 'Le pareció muy picante',
    pregunta: '¿Creía que todos los Takis picaban? ¿Le ofreciste Intense Nacho o Ranch sin picante? ¿Qué respondió?',
    solo: 'takis',
  },
  /*
   * Las de P&G (28-oct). Son las mismas cinco que muestra el tablero de P&G (precio, ya tiene, no
   * conoce, prefiere otra, presentación): con otros nombres, el cierre real no tendría dónde caer.
   */
  {
    valor: 'ya_tiene',
    etiqueta: 'Ya tiene en casa',
    pregunta: '¿Hace cuánto lo compró? ¿De qué marca es el que tiene?',
    solo: 'pg',
  },
  {
    valor: 'prefiere_otra',
    etiqueta: 'Prefiere otra marca',
    pregunta: '¿Cuál? ¿Te dijo por qué la prefiere?',
    solo: 'pg',
  },
  {
    valor: 'presentacion',
    etiqueta: 'Buscaba otra presentación',
    pregunta: '¿Cuál buscaba: otro tamaño, otro aroma, otro modelo?',
    solo: 'pg',
  },
  {
    valor: 'sin_gluten',
    etiqueta: 'Buscaba algo sin gluten',
    pregunta: '¿Preguntó por algo en particular?',
  },
  {
    valor: 'otra',
    etiqueta: 'Otra',
    pregunta: '¿Qué te dijeron?',
  },
];

/** «Ninguna en particular» no convive con las demás: marcarla apaga todo lo otro. */
export const OBJECION_NINGUNA = 'ninguna';

/**
 * El cuestionario de UNA campaña.
 *
 * Existe porque el cierre de Vital era el de Impulso Fijo con los productos cambiados, y
 * eso se notaba en pantalla: preguntaba por el precio de las Rapiditas Clásicas. Son
 * campañas distintas y sus reportes también, así que cada una define el suyo acá y el
 * formulario solo lo dibuja.
 */
export interface CuestionarioDeCierre {
  /** El precio propio que se pide de referencia. Cambia de producto según la campaña. */
  precioPropio: { label: string; placeholder: string };
  /** El precio de la competencia, contra el producto equivalente. */
  precioCompetencia: { label: string; placeholder: string };
  /** «¿Había impulsadora de otra marca?». Sale de `COMPETENCIA`, que es la que valida la base. */
  competencia: readonly Opcion[];
  /** Por qué no compraron. */
  objeciones: readonly OpcionDeObjecion[];
}

/**
 * Las objeciones de UN cuestionario: las generales más las marcadas con su nombre.
 *
 * Lista blanca a propósito. Los filtros eran negros (`solo !== 'vital'`) y con dos campañas
 * daba igual, pero la objeción de Takis se habría colado sola en fijas y en Vital.
 */
const objecionesDe = (cual: NonNullable<OpcionDeObjecion['solo']>) =>
  OBJECION.filter((o) => !o.retirada && (!o.solo || o.solo === cual));

export const CUESTIONARIO_FIJAS: CuestionarioDeCierre = {
  precioPropio: { label: 'Precio de las Rapiditas Clásicas hoy', placeholder: '6.90' },
  precioCompetencia: { label: 'Precio de la tortilla de la competencia', placeholder: 'No la vi' },
  competencia: COMPETENCIA,
  objeciones: objecionesDe('fijas'),
};

export const CUESTIONARIO_VITAL: CuestionarioDeCierre = {
  // El de 600 es el que la compañía puso como prioridad comercial en la capacitación.
  precioPropio: { label: 'Precio del pan Vital de 600 g hoy', placeholder: '12.90' },
  // Desde el 24-oct sin Cero Cero: el equivalente de Multicereal y Semillas, no el integral (usuario, 23-oct).
  precioCompetencia: {
    label: 'Precio del pan con semillas o multicereal de la competencia',
    placeholder: 'No lo vi',
  },
  competencia: COMPETENCIA,
  objeciones: objecionesDe('vital'),
};

export const CUESTIONARIO_TAKIS: CuestionarioDeCierre = {
  // Capacitación del 9-oct: el foco comercial son los sabores sin picante.
  precioPropio: { label: 'Precio de Takis Intense Nacho 90 g hoy (S/)', placeholder: 'Precio en tienda' },
  precioCompetencia: {
    label: 'Precio de Doritos queso hoy (S/)',
    placeholder: 'No lo vi',
  },
  /*
   * Sin «Sí, de Unión»: Unión es pan, la competencia de fijas y de Vital, no la de un snack. Se
   * queda en `COMPETENCIA` porque la base la acepta (CHECK de la 0090) y otros cierres la usan; acá
   * solo no se OFRECE. Lista blanca, para que otra marca de pan agregada mañana no se cuele.
   */
  competencia: COMPETENCIA.filter((o) => o.valor === 'no' || o.valor === 'otra'),
  objeciones: objecionesDe('takis'),
};

/** ¿La campaña es de Takis? Por la palabra y no por la marca exacta: cambia de mes a mes. */
const esDeTakis = (marca?: string | null): boolean => /takis/i.test(marca ?? '');

/**
 * ¿La campaña es de P&G? Por el CLIENTE, no por la marca: las marcas de P&G son muchas (Gillette,
 * Oral-B, Downy, Secret, Makro multimarca…) y adivinarlas por el nombre dejaría afuera la próxima.
 */
const esDePG = (cliente?: string | null): boolean => /p\s*&\s*g|procter/i.test(cliente ?? '');

/**
 * Las objeciones de P&G: lista blanca, porque las generales de Bimbo («buscaba algo sin gluten»)
 * no tienen sentido frente a una góndola de suavizantes o de afeitado.
 */
const OBJECIONES_PG = ['ninguna', 'precio', 'no_conoce', 'ya_tiene', 'prefiere_otra', 'presentacion', 'otra'];
const objecionesPG = OBJECION.filter((o) => OBJECIONES_PG.includes(o.valor));

/**
 * El precio de referencia de cada campaña de P&G, contra su equivalente de la competencia (28-oct).
 * La primera regla que calce con la marca manda; Makro multimarca cae en la general.
 */
const PRECIOS_PG: readonly { si: RegExp; propio: string; competencia: string }[] = [
  { si: /gillette|venus/i, propio: 'Precio de Gillette Prestobarba3 hoy (S/)',
    competencia: 'Precio de la desechable de la competencia (Schick o BIC)' },
  { si: /oral/i, propio: 'Precio del cepillo eléctrico Oral-B iO hoy (S/)',
    competencia: 'Precio del cepillo eléctrico de la competencia' },
  { si: /downy/i, propio: 'Precio de Downy Intense Fresh 2.6 L hoy (S/)',
    competencia: 'Precio del suavizante de la competencia (Suavitel)' },
  { si: /secret/i, propio: 'Precio de Secret Clinical hoy (S/)',
    competencia: 'Precio del desodorante clínico de la competencia (Rexona Clinical)' },
  { si: /head|h\s*&\s*s|shoulders/i, propio: 'Precio de Head & Shoulders 375 ml hoy (S/)',
    competencia: 'Precio del anticaspa de la competencia (Clear)' },
];

/** El cuestionario de una campaña de P&G: competencia sin «Unión» (es pan) y sus objeciones. */
export function cuestionarioPG(marca?: string | null): CuestionarioDeCierre {
  const p = PRECIOS_PG.find((x) => x.si.test(marca ?? ''));
  return {
    precioPropio: { label: p?.propio ?? 'Precio del producto que más impulsaste hoy (S/)', placeholder: 'Precio en tienda' },
    precioCompetencia: { label: p?.competencia ?? 'Precio del equivalente de la competencia (S/)', placeholder: 'No lo vi' },
    competencia: COMPETENCIA.filter((o) => o.valor === 'no' || o.valor === 'otra'),
    objeciones: objecionesPG,
  };
}

/**
 * El cuestionario que le toca a la campaña del turno.
 *
 * La MARCA va antes que el canje, porque «sin canje» ya no quiere decir «fijas»: Takis no tiene
 * canje y con la regla vieja caía en el de fijas, que pregunta por las Rapiditas. Es el mismo
 * error que tuvo el cierre de Vital el 12-set (28 cierres con el formulario de fijas, 0135).
 */
export function cuestionarioDe(
  conCanje: boolean, marca?: string | null, cliente?: string | null,
): CuestionarioDeCierre {
  // P&G primero y por el cliente: sin esto, toda campaña de P&G caía en el de fijas («precio de las
  // Rapiditas», «prefiere pan de molde»). Sin cliente, todo sigue como antes.
  if (esDePG(cliente)) return cuestionarioPG(marca);
  if (esDeTakis(marca)) return CUESTIONARIO_TAKIS;
  return conCanje ? CUESTIONARIO_VITAL : CUESTIONARIO_FIJAS;
}

/**
 * Las objeciones que ofrece la ventana de la OFICINA (completar o editar un cierre).
 *
 * Esa ventana nació antes que los cuestionarios y les ofrece a fijas y a Vital las dos listas
 * juntas; para ellas se deja igual. Takis ve solo las suyas: si no, a un cierre de Takis se le
 * ofrecería «Prefiere pan de molde», y a uno de fijas «Le pareció muy picante».
 *
 * Lo ya guardado se ofrece siempre, aunque no sea de la lista: una casilla marcada que no se
 * dibuja no se puede desmarcar.
 */
export function objecionesDeLaOficina(
  marca: string | null | undefined,
  guardadas: readonly string[] = [],
  cliente?: string | null,
): OpcionDeObjecion[] {
  const ofrecidas = esDePG(cliente)
    ? objecionesPG
    : esDeTakis(marca)
      ? CUESTIONARIO_TAKIS.objeciones
      : OBJECION.filter((o) => !o.retirada && (!o.solo || o.solo === 'fijas' || o.solo === 'vital'));
  return OBJECION.filter((o) => ofrecidas.includes(o) || guardadas.includes(o.valor));
}

/**
 * «¿Había impulsadora de otra marca?» en la ventana de la OFICINA.
 *
 * Fijas y Vital, la lista de siempre. Takis, la suya (sin Unión). Un valor ya guardado se ofrece
 * igual aunque no sea de la lista: un desplegable sin esa opción se ve como «No vi a nadie» y nadie
 * se entera de que hay algo que corregir.
 */
export function competenciaDeLaOficina(
  marca: string | null | undefined,
  guardada?: string | null,
  cliente?: string | null,
): readonly Opcion[] {
  if (esDePG(cliente)) {
    return COMPETENCIA.filter((o) => o.valor === 'no' || o.valor === 'otra' || o.valor === guardada);
  }
  if (!esDeTakis(marca)) return COMPETENCIA;
  return COMPETENCIA.filter(
    (o) => CUESTIONARIO_TAKIS.competencia.includes(o) || o.valor === guardada,
  );
}

/** Una objeción marcada, con lo que la impulsadora escribió sobre ella. */
export interface ObjecionElegida {
  valor: string;
  comentario: string;
}

/** ¿Algo impidió trabajar normal? Explica un turno flojo sin culpar a la persona. */
export const INCIDENCIA: readonly Opcion[] = [
  { valor: 'ninguna', etiqueta: 'Nada, todo normal' },
  { valor: 'no_ingreso', etiqueta: 'La tienda no me dejó ingresar' },
  { valor: 'sin_producto', etiqueta: 'No había producto para vender' },
  { valor: 'me_movieron', etiqueta: 'Me movieron de zona' },
  { valor: 'personal', etiqueta: 'Un problema personal o de salud' },
  { valor: 'otra', etiqueta: 'Otra cosa' },
];

export interface VentaSku {
  sku_code: string;
  unidades: number;
}

export interface CierreDeTurno {
  campaign_id: string;
  pdv_id: string;
  assignment_id: string | null;
  fecha: string;
  ventas: VentaSku[];
  quiebre: string;
  quiebre_skus: string[];
  exhibicion_adicional: string;
  precio_referencia: string;
  ejecucion_comentario: string;
  competencia_impulsadora: string;
  competencia_cual: string;
  competencia_exhibicion: string;
  competencia_precio: string;
  competencia_comentario: string;
  objeciones: ObjecionElegida[];
  incidencia: string;
  incidencia_detalle: string;
  /* --- Solo para campañas con dinámica de canje (`campaigns.cierre_con_canje`) --- */
  /** Dónde trabajó: góndola de desayunos, cabecera, ruma o lateral. */
  espacio: string;
  /** Si apareció el mercaderista de Bimbo, que es quien abastece. */
  mercaderista: string;
  /**
   * TOTAL de premios entregados. No se teclea: es la suma de los dos de abajo, y se sigue
   * mandando para que los cierres anteriores al 13-set, que solo tienen el total, se puedan
   * comparar con los nuevos sin ningún caso especial.
   */
  premios_entregados: string;
  /** TOTAL de premios que le quedan al cerrar. Tampoco se teclea: es la suma. */
  premios_quedan: string;
  /*
   * Se cuentan POR PREMIO y no en un número solo. El 12-set las impulsadoras declararon 237
   * entregas contra 205 que registró el juego en esas mismas tiendas: 35 unidades salieron sin
   * quedar anotadas. Con el total suelto no se puede saber de qué premio, y para reponer merch
   * eso es lo único que sirve; además el juego reparte según el stock que cree tener, así que si
   * el físico y la tabla se separan por premio, el juego sigue ofreciendo el que ya no está.
   */
  shaker_entregados: string;
  porta_entregados: string;
  /** Los otros tres premios del juego, desde el 10-oct (0324): el conteo pedía solo los dos primeros. */
  vaso_entregados: string;
  toalla_entregados: string;
  ventilador_entregados: string;
  shaker_quedan: string;
  porta_quedan: string;
  vaso_quedan: string;
  toalla_quedan: string;
  ventilador_quedan: string;
  /** Foto del merch que deja en tienda, como data URI. */
  merch_foto: string;
  /**
   * Precios en góndola (pedido de Bimbo del 27-oct, 0367), ya con el precio en número. Opcional: solo lo mandan los cierres
   * de Vital y fijas, y el servidor (`guardar_precios_gondola`) descarta la línea que no sirve sin tumbar el cierre.
   */
  precios?: { categoria: string; marca: string; sku: string; producto: string; pvp: string; sugerido: boolean }[];
}

/** Dónde estuvo parada. Sale de la capacitación: cabeceras en cinco tiendas, rumas en punto
 *  caliente y unos 70 laterales; la góndola de desayunos es el lugar por defecto. */
export const ESPACIO: readonly Opcion[] = [
  { valor: 'gondola', etiqueta: 'Góndola de desayunos' },
  { valor: 'cabecera', etiqueta: 'Cabecera' },
  { valor: 'ruma', etiqueta: 'Ruma en pasillo central' },
  { valor: 'lateral', etiqueta: 'Lateral' },
  { valor: 'otro', etiqueta: 'Otro sitio' },
];

/** El mercaderista de Bimbo es planilla del cliente y es quien repone. En Tottus es donde más
 *  falta, y sin él la impulsadora no puede sacar producto del almacén. */
export const MERCADERISTA: readonly Opcion[] = [
  { valor: 'si', etiqueta: 'Sí, estuvo' },
  { valor: 'no', etiqueta: 'No vino' },
  { valor: 'no_se', etiqueta: 'No sé / no lo ubiqué' },
];

/**
 * Los premios del juego, en el orden en que se cuentan. Son los cinco de `juego_stock` desde el
 * 10-oct; hasta el 23-oct el cierre pedía solo shaker y porta yogurt, así que lo entregado de los
 * otros tres no quedaba en ningún conteo (usuario, 23-oct: agregarlos).
 */
export const PREMIOS_DEL_MERCH = [
  { clave: 'shaker', nombre: 'Shaker', plural: 'SHAKERS' },
  { clave: 'porta', nombre: 'Porta yogurt', plural: 'PORTA YOGURT' },
  { clave: 'vaso', nombre: 'Vaso térmico', plural: 'VASOS TÉRMICOS' },
  { clave: 'toalla', nombre: 'Toalla', plural: 'TOALLAS' },
  { clave: 'ventilador', nombre: 'Ventilador', plural: 'VENTILADORES' },
] as const;

type CampoDeMerch = `${(typeof PREMIOS_DEL_MERCH)[number]['clave']}_${'entregados' | 'quedan'}`;

/** Los diez conteos de merch, con el texto que se le muestra a ella si falta alguno: primero lo entregado. */
export const CUENTAS_DE_MERCH: readonly { campo: CampoDeMerch; premio: string; que: string }[] = [
  ...PREMIOS_DEL_MERCH.map((p) => ({
    campo: `${p.clave}_entregados` as const, premio: p.nombre, que: `cuántos ${p.plural} entregaste hoy`,
  })),
  ...PREMIOS_DEL_MERCH.map((p) => ({
    campo: `${p.clave}_quedan` as const, premio: p.nombre, que: `cuántos ${p.plural} te quedan`,
  })),
];

/** Suma de lo entregado. Es lo que va al total, para no pedirle a ella que sume. */
export function entregadosEnTotal(c: Pick<CierreDeTurno, `${(typeof PREMIOS_DEL_MERCH)[number]['clave']}_entregados`>) {
  return PREMIOS_DEL_MERCH.reduce((s, p) => s + (Number(c[`${p.clave}_entregados`]) || 0), 0);
}

/** Suma de lo que queda en tienda. */
export function quedanEnTotal(c: Pick<CierreDeTurno, `${(typeof PREMIOS_DEL_MERCH)[number]['clave']}_quedan`>) {
  return PREMIOS_DEL_MERCH.reduce((s, p) => s + (Number(c[`${p.clave}_quedan`]) || 0), 0);
}

/**
 * Lo que falta para poder enviar, o `null` si está listo.
 *
 * Es deliberadamente permisivo: **cero ventas es una respuesta válida** —hay turnos en que no se
 * vende nada, y forzar un número mayor que cero es pedirle a la persona que invente—. Lo único que
 * se exige es que el número sea un número, que haya turno al que colgarlo, y que quien elige «otra
 * cosa» diga cuál: sin eso la opción no aporta nada al tablero.
 */
export function loQueFaltaEnCierre(
  c: CierreDeTurno,
  opciones: { turnoYaElegido?: boolean; conCanje?: boolean } = {},
): string | null {
  /*
   * Campañas con canje: el conteo de premios SÍ se exige, porque es el único cruce contra lo que
   * dice el juego, y cero es una respuesta válida. Se piden por premio y por separado, no el total. La foto se exige solo si QUEDA merch: si ya se
   * acabó no hay nada que fotografiar, y pedirla igual sería pedir una foto de la nada.
   *
   * Nace del pedido de Bimbo en la capacitación: «tomen foto para decir cuántas unidades de cada
   * cosa dejan y con eso nos respaldamos», porque en Cencosud el sobrante se queda en tienda.
   */
  if (opciones.conCanje) {
    for (const { campo, que } of CUENTAS_DE_MERCH) {
      const v = (c[campo] ?? '').trim();
      if (v === '' || Number.isNaN(Number(v))) {
        return `Falta ${que}. Si es ninguno, pon 0.`;
      }
    }
    if (!c.espacio) return 'Falta decir en qué espacio trabajaste.';
    /*
     * LA FOTO DEL MERCH ES OPCIONAL. Se exigía, y el usuario la liberó el 13-set: «he tenido
     * problemas con esa pregunta».
     *
     * El motivo por el que se pedía sigue en pie —Bimbo la pidió en la capacitación para
     * respaldar el sobrante que queda en tienda—, pero una validación que impide cerrar es peor
     * que una foto que falta: deja a la persona sin poder reportar NADA, y con ella se pierden
     * las ventas, las objeciones y el conteo de merch, que es lo que de verdad se cruza. El
     * sábado 12 se perdieron 23 cierres por una validación que no dejaba avanzar.
     */
  }
  /*
   * La primera comprobacion es del CELULAR y solo de ahi.
   *
   * En la app la persona escribe una fecha y el sistema tiene que LOCALIZAR su turno para saber a
   * que campania y a que PDV pertenece el cierre; si no lo encuentra, no hay donde colgarlo y el
   * mensaje la manda a revisar la fecha.
   *
   * En el panel el turno se eligio haciendo clic en su fila, y `registrar_cierre_manual` saca el
   * PDV, el recurso y la fecha de la asignacion —justamente para que nadie pueda registrar a
   * nombre de otro—. Exigir esos dos campos ahi seria pedirle a la pantalla un dato que no
   * necesita y que no tiene, y el formulario quedaria bloqueado sin poder explicar por que.
   *
   * Lo descubrio una prueba de la ventana nueva, no la revision del codigo.
   */
  if (!opciones.turnoYaElegido && (!c.campaign_id || !c.pdv_id)) {
    return 'No encontramos tu turno de esa fecha. Revisa la fecha o avisa a tu supervisor.';
  }
  if (c.ventas.some((v) => !Number.isInteger(v.unidades) || v.unidades < 0)) {
    return 'Las unidades vendidas tienen que ser números enteros (0 si no vendiste).';
  }
  if (c.quiebre === 'algunos' && c.quiebre_skus.length === 0) {
    return 'Marca qué productos faltaron en góndola.';
  }
  if (c.competencia_impulsadora === 'otra' && c.competencia_cual.trim() === '') {
    return 'Escribe de qué marca era la impulsadora que viste.';
  }
  // «Otra» sin texto no dice nada: es la única objeción cuyo comentario es obligatorio. Las demás
  // ya se nombran solas, y exigirles texto produciría guiones para pasar la puerta.
  if (c.objeciones.some((o) => o.valor === 'otra' && o.comentario.trim() === '')) {
    return 'Escribe qué te dijeron en la objeción «Otra».';
  }
  if (c.incidencia !== 'ninguna' && c.incidencia_detalle.trim() === '') {
    return 'Cuéntanos en una línea qué pasó.';
  }
  for (const [campo, valor] of [
    ['El precio de nuestro producto', c.precio_referencia],
    ['El precio de la competencia', c.competencia_precio],
  ] as [string, string][]) {
    if (valor.trim() !== '' && !/^\d+(\.\d{1,2})?$/.test(valor.trim())) {
      return `${campo} tiene que ser un número en soles (por ejemplo 6.90).`;
    }
  }
  return null;
}

/** Total de unidades reportadas. Es el número que se cruza contra el sell-out. */
export function totalUnidades(ventas: VentaSku[]): number {
  return ventas.reduce((s, v) => s + (Number.isFinite(v.unidades) ? v.unidades : 0), 0);
}

/**
 * ¿Este cierre es un registro de EJEMPLO, sembrado para armar una pantalla?
 *
 * La marca es la palabra «EJEMPLO» en cualquiera de los textos libres. No es una heuristica: es la
 * convencion con la que se sembraron, y el endpoint del tablero del cliente ya la usa para
 * excluirlos —*«los de EJEMPLO no entran: no son operacion»*—.
 *
 * ⚠️ Existe aca porque la BANDEJA no los filtraba y el tablero si, asi que las dos pantallas
 * discrepaban: la bandeja decia 8365 unidades y el tablero 8322, exactamente los 43 de los dos
 * registros sembrados. El usuario lo vio como *«¿por que sale s/r en Surco si en la bandeja si
 * esta?»*. Un mismo dato con dos totales distintos segun la pantalla es la peor clase de error de
 * tablero, porque las dos cifras parecen creibles.
 *
 * Se comparte en `domain/` a proposito: si manana cambia la convencion, cambia en un solo lugar y
 * no en el endpoint por un lado y en la bandeja por el otro.
 */
export function esCierreDeEjemplo(...textos: (string | null | undefined)[]): boolean {
  return textos.some((t) => typeof t === 'string' && t.includes('EJEMPLO'));
}

/* ==========================================================================
   La bandeja del panel: de qué turnos se espera un cierre
   ========================================================================== */

/**
 * En qué situación está el cierre de un turno asignado.
 *
 * Existe porque el usuario pidió ver la lista COMPLETA de posiciones asignadas y no solo los
 * cierres que existen: *«ver de los asientos asignados quiénes faltan llenar cierres de turno»*.
 * Con la bandeja anterior eso era imposible por construcción —leía `impulso_reports`, y una fila
 * que no existe no se lista—.
 *
 * ⚠️ **Pero «lista completa» sin estado MIENTE, y ya mintió una vez.** En el tablero del cliente los
 * turnos EN CURSO se pintaban como «sin registro», y el usuario lo cazó: *«sigo sin entender por qué
 * salen sin registros si aún no acaba el turno»*. La causa era exactamente esta: faltaba un estado,
 * así que un dato incompleto se leyó como una acusación. Acá el mismo error costaría más, porque
 * esta pantalla es la que decide a quién se le reclama.
 *
 * Por eso hay siete estados y **solo uno cuenta como deuda**.
 */
export type EstadoDeCierre =
  /** Hay cierre. Es un hecho y gana sobre todo lo demás. */
  | 'con_cierre'
  /** Trabajó y no hay cierre. **El único que es deuda**, y el único con botón de completar. */
  | 'falta_cierre'
  /** El turno todavía no terminó. No se le puede exigir algo que se llena al cerrar. */
  | 'en_curso'
  /** Día pasado sin ninguna marca: no trabajó, así que no hay turno que cerrar. */
  | 'sin_marca'
  /** Falta registrada. Nunca va a tener cierre, y pintarla en rojo para siempre es ruido. */
  | 'ausencia'
  /** Todavía no ocurrió. */
  | 'futuro'
  /** Posición sin nadie asignado: no hay a quién pedirle el cierre. */
  | 'sin_asignar';

/** Lo que hace falta saber de un turno para clasificarlo. Nada más: es lógica pura y comprobable. */
export interface TurnoParaClasificar {
  /** La fecha del turno, `YYYY-MM-DD`. */
  fecha: string;
  /** Hoy en Lima, `YYYY-MM-DD`. Se pasa como dato para que la función no dependa del reloj. */
  hoy: string;
  /** ¿Hay asignación viva (`propuesta` o `aprobada`)? */
  tieneAsignacion: boolean;
  tieneIngreso: boolean;
  tieneSalida: boolean;
  /**
   * ¿Tiene una autorizacion aprobada que signifique que NO trabajo?
   *
   * No es «tiene alguna autorizacion»: una de tipo `tardanza` dice justo lo contrario. Se
   * filtra con `AUTORIZACIONES_DE_AUSENCIA` antes de llegar aca.
   */
  tieneAusencia: boolean;
  tieneCierre: boolean;
}

/**
 * Los tipos de autorizacion que significan QUE NO TRABAJO.
 *
 * No todas lo significan, y confundirlas produjo un error real: `authorizations.kind` admite
 * `permiso` —que puede ser una falta justificada— y `tardanza`, que es lo contrario: la persona
 * llego tarde, o sea que FUE. Tratar cualquier autorizacion aprobada como una ausencia hizo que la
 * bandeja rotulara «Ausencia» un turno trabajado de 09:55 a 18:09.
 *
 * Es una lista blanca y no una negra a proposito: si manana aparece un `kind` nuevo, el peor
 * resultado posible es que un permiso quede sin reconocer —la fila dira «No marco», que es lo que
 * se ve en las marcas—, y no que un turno trabajado aparezca como falta.
 */
export const AUTORIZACIONES_DE_AUSENCIA: readonly string[] = ['permiso'];

/**
 * Clasifica un turno asignado según qué se le puede exigir.
 *
 * El ORDEN de las reglas es la parte que importa, y cada salto de fila tiene su razón:
 *
 *  1. **Sin asignación** primero: no hay persona, no hay nada que reclamar.
 *  2. **Con cierre** antes que todo lo demás. Si la fila existe, existe: cuando el dato se
 *     contradice —una falta registrada y un cierre el mismo día— la pantalla tiene que mostrar el
 *     registro y no esconderlo detrás de la falta. Un cierre invisible no se puede corregir.
 *  3. **Futuro**: una posición de la semana que viene no es una falta todavía.
 *  4. **En curso**: hoy y sin salida. Cubre los dos casos —marcó ingreso y sigue trabajando, o
 *     todavía no llegó— y en los dos la respuesta es la misma: el día no terminó.
 *  5. ⚠️ **HABER MARCADO INGRESO GANA SOBRE CUALQUIER AUTORIZACIÓN.** Es la correccion de un error
 *     propio: la version anterior preguntaba por la ausencia ANTES que por el ingreso, asi que una
 *     persona con permiso aprobado que igual fue a trabajar aparecia como «Ausencia» con su ingreso
 *     y su salida a la vista. Si hay marca de entrada, estuvo: eso no lo discute ningun permiso.
 *  6. **Ausencia**: no marcó y tiene una autorización que significa que no trabajó.
 *  7. **Sin marca**: no marcó y no hay nada que lo explique.
 */
export function estadoDelCierre(t: TurnoParaClasificar): EstadoDeCierre {
  if (!t.tieneAsignacion) return 'sin_asignar';
  if (t.tieneCierre) return 'con_cierre';
  if (t.fecha > t.hoy) return 'futuro';
  if (t.fecha === t.hoy && !t.tieneSalida) return 'en_curso';
  if (t.tieneIngreso) return 'falta_cierre';
  if (t.tieneAusencia) return 'ausencia';
  return 'sin_marca';
}

/** Cómo se nombra cada estado en pantalla. Fuente única: la usan la bandeja y el Excel. */
export const ESTADO_DE_CIERRE_LABEL: Record<EstadoDeCierre, string> = {
  con_cierre: 'Con cierre',
  falta_cierre: 'Falta el cierre',
  en_curso: 'Turno en curso',
  sin_marca: 'No marcó',
  ausencia: 'Ausencia',
  futuro: 'Programado',
  sin_asignar: 'Sin asignar',
};

/**
 * ¿Este estado es una deuda que hay que reclamar?
 *
 * Es una función y no un `=== 'falta_cierre'` suelto porque de esto sale el contador de pendientes
 * que la gente va a mirar para decidir a quién llamar. Escrito en un solo lugar, el día que se
 * agregue un estado nuevo no queda un contador contando otra cosa.
 */
export function esDeudaDeCierre(estado: EstadoDeCierre): boolean {
  return estado === 'falta_cierre';
}
